// src/components/auth/LoginForm.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert, 
  InputAdornment, 
  IconButton,
  CircularProgress,
  Fade,
  Card,
  CardContent,
  Container
} from '@mui/material';
import { 
  Lock as LockIcon,
  Visibility, 
  VisibilityOff, 
  ArrowBack as BackIcon,
  CheckCircleOutline as SuccessIcon,
  Person as PersonIcon,
  Email as EmailIcon
} from '@mui/icons-material';

const LoginForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Estados para vistas y navegación
  const [view, setView] = useState('login'); // 'login', 'forgot', 'reset'
  const [resetToken, setResetToken] = useState('');
  
  // Estados para credenciales de Login
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Estados para Recuperación (Forgot Password)
  const [email, setEmail] = useState('');
  
  // Estados para Restablecer (Reset Password)
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Estados de carga, error y éxito
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    company_name: 'YinoWISP',
    company_logo: ''
  });

  // Detectar token en la URL al montar o cambiar ubicación
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      console.log('Token de recuperación detectado en URL:', token);
      setResetToken(token);
      setView('reset');
      setError('');
      setSuccess('');
    } else {
      setView('login');
    }
  }, [location]);

  // Cargar configuraciones públicas de la empresa (Nombre y Logo)
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await fetch('/api/public/company-settings');
        if (response.ok) {
          const data = await response.json();
          setCompanySettings({
            company_name: data.company_name || 'YinoWISP',
            company_logo: data.company_logo || ''
          });
        }
      } catch (err) {
        console.error('Error fetching company settings for admin login:', err);
      }
    };
    fetchCompanySettings();
  }, []);

  // Manejar cambios en formulario de login
  const handleLoginChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  // Enviar inicio de sesión
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error en el inicio de sesión');
      }

      login(data.user, data.token);
    } catch (err) {
      console.error('Error en login:', err);
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  // Enviar solicitud de recuperación (Forgot Password)
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al solicitar recuperación');
      }

      setSuccess('Si el correo electrónico está registrado, recibirás un enlace de recuperación en unos momentos.');
      setEmail('');
    } catch (err) {
      console.error('Error en forgot-password:', err);
      setError(err.message || 'Ocurrió un error al procesar tu solicitud');
    } finally {
      setLoading(false);
    }
  };

  // Enviar restablecimiento de contraseña (Reset Password)
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetToken,
          password: newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al restablecer la contraseña');
      }

      setSuccess('Contraseña restablecida exitosamente. Redirigiendo al inicio de sesión...');
      setNewPassword('');
      setConfirmPassword('');
      
      // Esperar 3 segundos para redirigir visualmente de forma suave
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } catch (err) {
      console.error('Error en reset-password:', err);
      setError(err.message || 'El enlace de recuperación es inválido o ha expirado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        width: '100vw',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(circle at center, #0a0f24 0%, #02040a 100%)',
        py: 4,
        px: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '380px',
          height: '380px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, rgba(99, 102, 241, 0) 70%)',
          top: '10%',
          left: '10%',
          filter: 'blur(60px)',
          zIndex: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '380px',
          height: '380px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.06) 0%, rgba(168, 85, 247, 0) 70%)',
          bottom: '10%',
          right: '10%',
          filter: 'blur(60px)',
          zIndex: 1
        }
      }}
    >
      <Fade in={true} timeout={800}>
        <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 10 }}>
          <Card 
            elevation={0} 
            sx={{ 
              background: 'rgba(11, 15, 30, 0.8)',
              backdropFilter: 'blur(30px)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: '24px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }
            }}
          >
            <CardContent sx={{ p: { xs: 4, sm: 5 } }}>
              {/* VISTA: INICIO DE SESIÓN */}
              {view === 'login' && (
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    {companySettings.company_logo ? (
                      <Box
                        component="img"
                        src={companySettings.company_logo}
                        alt={companySettings.company_name}
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'contain',
                          mb: 2.5,
                          mx: 'auto',
                          display: 'block',
                          borderRadius: '24px',
                          filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.35))',
                          '@keyframes floatAndGlow': {
                            '0%, 100%': { transform: 'translateY(0px) scale(1)', filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.35))' },
                            '50%': { transform: 'translateY(-6px) scale(1.02)', filter: 'drop-shadow(0 0 25px rgba(168, 85, 247, 0.55))' },
                          },
                          animation: 'floatAndGlow 4s ease-in-out infinite'
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          width: '64px', 
                          height: '64px', 
                          borderRadius: '20px', 
                          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                          border: '2px solid rgba(99, 102, 241, 0.35)',
                          boxShadow: '0 0 20px rgba(99, 102, 241, 0.15)',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          margin: '0 auto 20px auto'
                        }}
                      >
                        <PersonIcon sx={{ fontSize: '32px', color: '#6366f1' }} />
                      </Box>
                    )}
                    
                    <Typography 
                      variant="h4" 
                      component="h1" 
                      sx={{ 
                        fontFamily: '"Outfit", "Inter", sans-serif', 
                        fontWeight: 900, 
                        background: 'linear-gradient(90deg, #38bdf8 0%, #c084fc 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.5px',
                        mb: 0.5
                      }}
                    >
                      YinoWISP
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: '"Outfit", "Inter", sans-serif', fontWeight: 600, fontSize: '0.85rem', letterSpacing: '0.5px', textTransform: 'uppercase', mt: 1 }}>
                      Panel Administrativo
                    </Typography>
                  </Box>

                  {error && (
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 3, 
                        borderRadius: '12px', 
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                        color: '#f87171',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        '& .MuiAlert-icon': { color: '#f87171' },
                        fontFamily: "'Outfit', sans-serif" 
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <TextField
                      fullWidth
                      label="Nombre de Usuario"
                      name="username"
                      value={credentials.username}
                      onChange={handleLoginChange}
                      disabled={loading}
                      required
                      variant="outlined"
                      InputLabelProps={{
                        sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#38bdf8' }, fontFamily: "'Outfit', sans-serif" }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        sx: { 
                          color: '#ffffff',
                          bgcolor: '#080c1a',
                          borderRadius: '12px', 
                          fontFamily: "'Outfit', sans-serif",
                          transition: 'all 0.3s ease',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.08)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#38bdf8',
                            boxShadow: '0 0 12px rgba(56, 189, 248, 0.15)'
                          },
                          '& input': {
                            '&:-webkit-autofill': {
                              WebkitBoxShadow: '0 0 0 100px #080c1a inset !important',
                              WebkitTextFillColor: '#ffffff !important',
                              transition: 'background-color 5000s ease-in-out 0s',
                            }
                          }
                        }
                      }}
                    />
                    <Box>
                      <TextField
                        fullWidth
                        label="Contraseña"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={credentials.password}
                        onChange={handleLoginChange}
                        disabled={loading}
                        required
                        variant="outlined"
                        InputLabelProps={{
                          sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#c084fc' }, fontFamily: "'Outfit', sans-serif" }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: '#c084fc', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: { 
                            color: '#ffffff',
                            bgcolor: '#080c1a',
                            borderRadius: '12px', 
                            fontFamily: "'Outfit', sans-serif",
                            transition: 'all 0.3s ease',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.08)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#c084fc',
                              boxShadow: '0 0 12px rgba(192, 132, 252, 0.15)'
                            },
                            '& input': {
                              '&:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 100px #080c1a inset !important',
                                WebkitTextFillColor: '#ffffff !important',
                                transition: 'background-color 5000s ease-in-out 0s',
                              }
                            }
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                disabled={loading}
                                sx={{ color: 'rgba(255, 255, 255, 0.45)', '&:hover': { color: '#c084fc' } }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                        <Button 
                          onClick={() => {
                            setView('forgot');
                            setError('');
                            setSuccess('');
                          }} 
                          disabled={loading}
                          sx={{ 
                            textTransform: 'none', 
                            fontSize: '13px', 
                            fontWeight: 600, 
                            color: '#38bdf8',
                            fontFamily: "'Outfit', sans-serif",
                            '&:hover': {
                              background: 'transparent',
                              color: '#c084fc',
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          ¿Olvidó su contraseña?
                        </Button>
                      </Box>
                    </Box>
                    
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '15px',
                        py: 1.5,
                        fontFamily: "'Outfit', sans-serif",
                        background: 'linear-gradient(95deg, #38bdf8 0%, #c084fc 100%)',
                        boxShadow: '0 4px 20px rgba(192, 132, 252, 0.3)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          background: 'linear-gradient(95deg, #0ea5e9 0%, #a855f7 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 24px rgba(192, 132, 252, 0.45)',
                        },
                        '&:active': {
                          transform: 'translateY(1px)',
                        },
                        '&.Mui-disabled': {
                          background: 'rgba(255, 255, 255, 0.12)',
                          color: 'rgba(255, 255, 255, 0.3)'
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Iniciar Sesión'}
                    </Button>
                  </form>
                </Box>
              )}

              {/* VISTA: SOLICITAR RECUPERACIÓN (FORGOT PASSWORD) */}
              {view === 'forgot' && (
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box 
                      sx={{ 
                        width: '64px', 
                        height: '64px', 
                        borderRadius: '20px', 
                        background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                        border: '2px solid rgba(168, 85, 247, 0.35)',
                        boxShadow: '0 0 20px rgba(168, 85, 247, 0.15)',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        margin: '0 auto 20px auto'
                      }}
                    >
                      <EmailIcon sx={{ fontSize: '32px', color: '#a855f7' }} />
                    </Box>
                    <Typography variant="h5" component="h1" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, color: '#ffffff', letterSpacing: '-0.5px' }}>
                      Recuperar Contraseña
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Outfit', sans-serif", mt: 1, px: 2 }}>
                      Ingresa tu correo electrónico registrado y te enviaremos un enlace seguro para restablecer tu contraseña.
                    </Typography>
                  </Box>

                  {error && (
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 3, 
                        borderRadius: '12px', 
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                        color: '#f87171',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        '& .MuiAlert-icon': { color: '#f87171' },
                        fontFamily: "'Outfit', sans-serif" 
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Box sx={{ textAlign: 'center', my: 3 }}>
                      <SuccessIcon sx={{ fontSize: '50px', color: '#10b981', mb: 1.5 }} />
                      <Alert 
                        severity="success" 
                        sx={{ 
                          borderRadius: '12px', 
                          bgcolor: 'rgba(34, 197, 94, 0.1)',
                          color: '#4ade80',
                          border: '1px solid rgba(34, 197, 94, 0.2)',
                          '& .MuiAlert-icon': { color: '#4ade80' },
                          fontFamily: "'Outfit', sans-serif", 
                          textAlign: 'left' 
                        }}
                      >
                        {success}
                      </Alert>
                      <Button
                        onClick={() => {
                          setView('login');
                          setError('');
                          setSuccess('');
                        }}
                        variant="outlined"
                        fullWidth
                        sx={{ 
                          mt: 3, 
                          borderRadius: '12px', 
                          textTransform: 'none', 
                          fontFamily: "'Outfit', sans-serif", 
                          fontWeight: 700,
                          color: '#38bdf8',
                          borderColor: 'rgba(56, 189, 248, 0.3)',
                          '&:hover': {
                            borderColor: '#38bdf8',
                            background: 'rgba(56, 189, 248, 0.05)'
                          }
                        }}
                      >
                        Volver al Inicio de Sesión
                      </Button>
                    </Box>
                  )}

                  {!success && (
                    <form onSubmit={handleForgotSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <TextField
                        fullWidth
                        label="Correo Electrónico"
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                        required
                        variant="outlined"
                        InputLabelProps={{
                          sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#38bdf8' }, fontFamily: "'Outfit', sans-serif" }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: { 
                            color: '#ffffff',
                            bgcolor: '#080c1a',
                            borderRadius: '12px', 
                            fontFamily: "'Outfit', sans-serif",
                            transition: 'all 0.3s ease',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.08)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#38bdf8',
                              boxShadow: '0 0 12px rgba(56, 189, 248, 0.15)'
                            },
                            '& input': {
                              '&:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 100px #080c1a inset !important',
                                WebkitTextFillColor: '#ffffff !important',
                                transition: 'background-color 5000s ease-in-out 0s',
                              }
                            }
                          }
                        }}
                      />
                      
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '15px',
                          py: 1.5,
                          fontFamily: "'Outfit', sans-serif",
                          background: 'linear-gradient(95deg, #38bdf8 0%, #c084fc 100%)',
                          boxShadow: '0 4px 20px rgba(192, 132, 252, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(95deg, #0ea5e9 0%, #a855f7 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 24px rgba(192, 132, 252, 0.45)',
                          }
                        }}
                      >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Enviar Enlace de Recuperación'}
                      </Button>

                      <Button
                        startIcon={<BackIcon />}
                        onClick={() => {
                          setView('login');
                          setError('');
                          setSuccess('');
                        }}
                        disabled={loading}
                        fullWidth
                        sx={{
                          textTransform: 'none',
                          fontFamily: "'Outfit', sans-serif",
                          fontWeight: 600,
                          color: 'rgba(255, 255, 255, 0.45)',
                          mt: 1,
                          '&:hover': { 
                            color: '#38bdf8',
                            background: 'transparent' 
                          }
                        }}
                      >
                        Volver al inicio de sesión
                      </Button>
                    </form>
                  )}
                </Box>
              )}

              {/* VISTA: RESTABLECER CONTRASEÑA (RESET PASSWORD) */}
              {view === 'reset' && (
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box 
                      sx={{ 
                        width: '64px', 
                        height: '64px', 
                        borderRadius: '20px', 
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(56, 189, 248, 0.1) 100%)',
                        border: '2px solid rgba(16, 185, 129, 0.35)',
                        boxShadow: '0 0 20px rgba(16, 185, 129, 0.15)',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        margin: '0 auto 20px auto'
                      }}
                    >
                      <LockIcon sx={{ fontSize: '30px', color: '#10b981' }} />
                    </Box>
                    <Typography variant="h5" component="h1" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, color: '#ffffff', letterSpacing: '-0.5px' }}>
                      Restablecer Contraseña
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Outfit', sans-serif", mt: 1 }}>
                      Ingresa tu nueva contraseña para volver a tener acceso a tu panel de administración.
                    </Typography>
                  </Box>

                  {error && (
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 3, 
                        borderRadius: '12px', 
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                        color: '#f87171',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        '& .MuiAlert-icon': { color: '#f87171' },
                        fontFamily: "'Outfit', sans-serif" 
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Box sx={{ textAlign: 'center', my: 3 }}>
                      <SuccessIcon sx={{ fontSize: '50px', color: '#10b981', mb: 1.5 }} />
                      <Alert 
                        severity="success" 
                        sx={{ 
                          borderRadius: '12px', 
                          bgcolor: 'rgba(34, 197, 94, 0.1)',
                          color: '#4ade80',
                          border: '1px solid rgba(34, 197, 94, 0.2)',
                          '& .MuiAlert-icon': { color: '#4ade80' },
                          fontFamily: "'Outfit', sans-serif" 
                        }}
                      >
                        {success}
                      </Alert>
                    </Box>
                  )}

                  {!success && (
                    <form onSubmit={handleResetSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <TextField
                        fullWidth
                        label="Nueva Contraseña"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                        required
                        variant="outlined"
                        InputLabelProps={{
                          sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#c084fc' }, fontFamily: "'Outfit', sans-serif" }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: '#c084fc', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: { 
                            color: '#ffffff',
                            bgcolor: '#080c1a',
                            borderRadius: '12px', 
                            fontFamily: "'Outfit', sans-serif",
                            transition: 'all 0.3s ease',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.08)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#c084fc',
                              boxShadow: '0 0 12px rgba(192, 132, 252, 0.15)'
                            },
                            '& input': {
                              '&:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 100px #080c1a inset !important',
                                WebkitTextFillColor: '#ffffff !important',
                                transition: 'background-color 5000s ease-in-out 0s',
                              }
                            }
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                edge="end"
                                disabled={loading}
                                sx={{ color: 'rgba(255, 255, 255, 0.45)', '&:hover': { color: '#c084fc' } }}
                              >
                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Confirmar Contraseña"
                        type={showNewPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        required
                        variant="outlined"
                        InputLabelProps={{
                          sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#c084fc' }, fontFamily: "'Outfit', sans-serif" }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: '#c084fc', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: { 
                            color: '#ffffff',
                            bgcolor: '#080c1a',
                            borderRadius: '12px', 
                            fontFamily: "'Outfit', sans-serif",
                            transition: 'all 0.3s ease',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.08)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#c084fc',
                              boxShadow: '0 0 12px rgba(192, 132, 252, 0.15)'
                            },
                            '& input': {
                              '&:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 100px #080c1a inset !important',
                                WebkitTextFillColor: '#ffffff !important',
                                transition: 'background-color 5000s ease-in-out 0s',
                              }
                            }
                          }
                        }}
                      />
                      
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '15px',
                          py: 1.5,
                          fontFamily: "'Outfit', sans-serif",
                          background: 'linear-gradient(95deg, #38bdf8 0%, #c084fc 100%)',
                          boxShadow: '0 4px 20px rgba(192, 132, 252, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(95deg, #0ea5e9 0%, #a855f7 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 24px rgba(192, 132, 252, 0.45)',
                          }
                        }}
                      >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Guardar Nueva Contraseña'}
                      </Button>
                    </form>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Fade>
    </Box>
  );
};

export default LoginForm;